import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useSignalR } from '@hooks/useSignalR';
import { useTranslation } from '@stores/translationStore';
import { playSound } from '../services/soundService';
import SecureAttachment from '../components/SecureAttachment';
import StatusCard, { StatusCardMetadata } from '../components/StatusCard';
import MenuCard, { MenuCardMetadata } from '../components/MenuCard';
import LaundryMenuCard, { LaundryMenuCardMetadata } from '../components/LaundryMenuCard';
import PhotoCartCard, { PhotoCartMetadata } from '../components/PhotoCartCard';
import PageSpinner from '@components/PageSpinner';
import { createPortal } from 'react-dom';
import CallDock from '@components/CallDock';
import { useSessionStore } from '@stores/sessionStore';
import './MessagesPage.scss';
import '../components/PhoneVerificationModal.scss';

interface ParsedMetadata {
  statusCard?: StatusCardMetadata;
  menuCard?: MenuCardMetadata;
  laundryMenuCard?: LaundryMenuCardMetadata;
  photoCart?: PhotoCartMetadata;
}

interface Message {
  id: string;
  senderType: string;
  senderName?: string;
  content: string;
  sentAt: string;
  metadata?: string | null;
  parsedMetadata?: ParsedMetadata | null;
  attachments?: { fileUrl: string; fileName: string; contentType: string; fileSize: number }[];
  /** Emoji reactions on this message. */
  reactions?: { emoji: string; count: number; by: string[]; guest: boolean; staff: boolean }[] | null;
  /** The message this one answers (a quoted reply). */
  replyTo?: { id: string; senderType: string; senderName?: string; snippet: string } | null;
  /** A photo on its way (shown from the phone while it uploads), so the guest sees it was taken. */
  sendingPhoto?: string;
}

function parseMetadata(raw?: string | null): ParsedMetadata | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.type) return null;

    if (parsed.type === 'menu' && parsed.stationId) {
      return { menuCard: parsed as MenuCardMetadata };
    }

    if (parsed.type === 'laundry_menu') {
      return { laundryMenuCard: parsed as LaundryMenuCardMetadata };
    }

    if (parsed.type === 'photo_cart' && parsed.cartId) {
      return { photoCart: parsed as PhotoCartMetadata };
    }

    if (parsed.referenceId) {
      return { statusCard: parsed as StatusCardMetadata };
    }

    return null;
  } catch { return null; }
}

/** Parse numbered options (e.g. "1. Room cleaning\n2. Fresh towels") from AI messages */
function parseNumberedOptions(text: string): { number: string; label: string }[] {
  if (!text) return [];
  const lines = text.split('\n');
  const options: { number: string; label: string }[] = [];
  for (const line of lines) {
    const m = line.match(/^\s*(\d+)[.)]\s+(.+)/);
    if (m) options.push({ number: m[1], label: m[2].trim() });
  }
  // Only return if we found at least 2 consecutive numbered options
  return options.length >= 2 ? options : [];
}

/** Render message text with numbered options as clickable inline buttons */
function renderInlineOptions(
  text: string,
  options: { number: string; label: string }[],
  onSelect: (opt: { number: string; label: string }) => void
): React.ReactNode {
  const optionNumbers = new Set(options.map(o => o.number));
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const m = line.match(/^\s*(\d+)[.)]\s+(.+)/);
    if (m && optionNumbers.has(m[1])) {
      const opt = options.find(o => o.number === m[1])!;
      elements.push(
        <span
          key={`opt-${m[1]}`}
          className="inline-option"
          role="button"
          tabIndex={0}
          onClick={() => onSelect(opt)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSelect(opt); }}
        >
          <span className="inline-option-num">{m[1]}</span>
          {m[2].trim()}
        </span>
      );
    } else {
      elements.push(<span key={`line-${i}`}>{line}</span>);
    }
    if (i < lines.length - 1) elements.push(<br key={`br-${i}`} />);
  }

  return <>{elements}</>;
}

interface WhatsAppStatus {
  hasWhatsApp: boolean;
  isVerified: boolean;
  whatsAppPhone?: string;
  verifiedAt?: string;
}

export default function MessagesPage() {
  const { t } = useTranslation();
  const { on } = useSignalR();
  const [messages, setMessages] = useState<Message[]>([]);
  // The AI voice call runs docked above this chat (?call=1 from the home screen), so everything it does —
  // the transcript, menu and order cards, request updates, photos — lands in this thread as it happens.
  const [searchParams, setSearchParams] = useSearchParams();
  const voiceCallAvailable = useSessionStore(s => s.session?.availableModules?.includes('voice_call') ?? false);
  const [callOpen, setCallOpen] = useState(() => searchParams.get('call') === '1');
  // While a call is live the face can take the screen (only the live words under it) or shrink back so the
  // thread and the input are in view; the dock switches, a card from the assistant brings the chat back.
  const [callFocus, setCallFocus] = useState(false);
  const callFocusRef = useRef(false);
  useEffect(() => { callFocusRef.current = callFocus; }, [callFocus]);
  const closeCall = () => {
    setCallOpen(false);
    if (searchParams.has('call')) {
      const next = new URLSearchParams(searchParams);
      next.delete('call');
      setSearchParams(next, { replace: true });
    }
  };
  // One conversation covers the whole stay: where before arrival, the stay and after checkout begin.
  const [stages, setStages] = useState<StageSpan[]>([]);
  // Replying to a specific message: shown above the input, sent with the message, quoted in the thread.
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  // The message whose emoji picker is open.
  const [reactingTo, setReactingTo] = useState<string | null>(null);
  // The picker closes after a pick or any tap elsewhere.
  // Touch and hold a message opens the picker, as on WhatsApp.
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedAt = useRef(0);
  const startPress = (id: string) => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      openedAt.current = Date.now();
      setReactingTo(id);
      navigator.vibrate?.(10);
    }, 450);
  };
  const endPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  useEffect(() => {
    if (!reactingTo) return;
    const close = () => { if (Date.now() - openedAt.current > 400) setReactingTo(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [reactingTo]);

  /** React to a message; the same emoji again takes it back. A 👍 / 👎 to the AI's question answers it. */
  const react = async (messageId: string, emoji: string) => {
    setReactingTo(null);
    try {
      const { data } = await apiClient.post<{ reactions: Message['reactions'] }>(`/api/v1/guest/messages/${messageId}/reactions`, { emoji });
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, reactions: data.reactions } : m)));
    } catch (err) {
      console.error('Failed to react:', err);
    }
  };

  useEffect(() => {
    return on('MessageReaction', (raw: unknown) => {
      const data = raw as { messageId: string; reactions: Message['reactions'] };
      setMessages(prev => prev.map(m => (m.id === data.messageId ? { ...m, reactions: data.reactions } : m)));
    });
  }, [on]);
  // The thread loads the newest 50; "Earlier messages" loads the rest, page by page.
  const [hasOlder, setHasOlder] = useState(false);
  const [olderPage, setOlderPage] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const lastMessageIdRef = useRef<string | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<{ file: File; uploading: boolean; url?: string; error?: string }[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  // The page is exactly one screen tall and only the thread scrolls. iOS slides the page's scroll parent (and
  // the window) up to show the input above the keyboard and never slides it back, which left the call's face
  // under the top bar: once the keyboard is gone, both are put back.
  useEffect(() => {
    const outer = pageRef.current?.closest<HTMLElement>('.page-transition-container');
    const reset = () => {
      if (outer && outer.scrollTop !== 0) outer.scrollTop = 0;
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    const keyboardGone = () => {
      const vv = window.visualViewport;
      if (!vv || vv.height >= window.innerHeight - 80) reset();
    };
    const afterBlur = () => setTimeout(keyboardGone, 350);
    window.visualViewport?.addEventListener('resize', keyboardGone);
    document.addEventListener('focusout', afterBlur);
    reset();
    return () => {
      window.visualViewport?.removeEventListener('resize', keyboardGone);
      document.removeEventListener('focusout', afterBlur);
    };
  }, [loading]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialLoadRef = useRef(true);

  // Personal name prompt state (for multi-device rooms)
  const [needsName, setNeedsName] = useState(false);
  const [personalName, setPersonalName] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [nameSubmitting, setNameSubmitting] = useState(false);

  // WhatsApp verification state
  const [whatsAppStatus, setWhatsAppStatus] = useState<WhatsAppStatus | null>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  // The top bar's right slot (TopNavBar renders it before the page mounts).
  const [navActions, setNavActions] = useState<HTMLElement | null>(null);
  useEffect(() => setNavActions(document.getElementById('top-nav-actions')), []);
  const [waLoading, setWaLoading] = useState(false);
  const [waError, setWaError] = useState('');
  
  // WhatsApp verification step state
  const [waStep, setWaStep] = useState<'initial' | 'code' | 'verified'>('initial');

  
  // Manual verification state
  const [manualPhone, setManualPhone] = useState('');
  const [manualCountryCode, setManualCountryCode] = useState('+972');
  const [manualChallengeId, setManualChallengeId] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [manualMaskedPhone, setManualMaskedPhone] = useState('');
  const [forceSms, setForceSms] = useState(false);
  const [deliveryChannel, setDeliveryChannel] = useState<string>('');

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());

        // Strip codec params (e.g. "audio/webm;codecs=opus" → "audio/webm") so the server accepts it
        const blobMimeType = mimeType.split(';')[0];
        const audioBlob = new Blob(audioChunksRef.current, { type: blobMimeType });
        audioChunksRef.current = [];

        if (audioBlob.size === 0) return;

        // Upload and transcribe
        setIsTranscribing(true);
        try {
          const ext = mimeType.includes('webm') ? '.webm' : '.m4a';
          const formData = new FormData();
          formData.append('file', audioBlob, `recording${ext}`);
          const { data } = await apiClient.post<{ success: boolean; transcription?: string; error?: string }>('/api/v1/guest/messages/transcribe', formData);
          if (data.success && data.transcription) {
            setInput(prev => {
              const separator = prev.trim() ? ' ' : '';
              return prev + separator + data.transcription;
            });
            // Focus the input
            setTimeout(() => inputRef.current?.focus(), 50);
          } else if (data.error) {
            console.error('Transcription error:', data.error);
          }
        } catch (err) {
          console.error('Failed to transcribe audio:', err);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250); // collect data every 250ms
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Failed to access microphone:', err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      // Remove the onstop handler so transcription doesn't run
      mediaRecorderRef.current.onstop = () => {
        // Stop all tracks to release microphone
        mediaRecorderRef.current = null;
      };
      if (mediaRecorderRef.current.state !== 'inactive') {
        const stream = mediaRecorderRef.current.stream;
        mediaRecorderRef.current.stop();
        stream.getTracks().forEach(track => track.stop());
      }
    }
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  const formatRecordingTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const scrollToBottom = (instant = false) => {
    const doScroll = () => {
      const container = containerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    };
    if (instant) {
      // Double requestAnimationFrame to wait for layout
      requestAnimationFrame(() => requestAnimationFrame(doScroll));
      // Also do it after a delay for images/attachments loading
      setTimeout(doScroll, 100);
      setTimeout(doScroll, 300);
    } else {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }
  };

  const loadMessages = useCallback(async (signal?: AbortSignal) => {
    try {
      const { data } = await apiClient.get<{ messages: Message[]; needsName?: boolean; personalName?: string; stages?: StageSpan[]; hasMore?: boolean }>('/api/v1/guest/messages/conversation', { signal });
      setStages(data.stages || []);
      const msgs = (data.messages || []).map((m: Message) => ({
        ...m,
        parsedMetadata: parseMetadata(m.metadata),
      }));
      // A refresh of the newest page keeps any older messages already loaded above it.
      setMessages(prev => {
        const ids = new Set(msgs.map(m => m.id));
        const firstNew = msgs[0]?.sentAt;
        const older = prev.filter(m => !m.id.startsWith('temp-') && !ids.has(m.id) && (!firstNew || m.sentAt < firstNew));
        if (older.length === 0) setHasOlder(!!data.hasMore);
        return [...older, ...msgs];
      });
      // Check if guest needs to set their name (multi-device room)
      if (data.needsName != null) setNeedsName(data.needsName);
      if (data.personalName) setPersonalName(data.personalName);
      // Mark as read
      await apiClient.post('/api/v1/guest/messages/read').catch(() => {});
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load WhatsApp verification status
  const loadWhatsAppStatus = useCallback(async (signal?: AbortSignal) => {
    try {
      const { data } = await apiClient.get<WhatsAppStatus>('/api/auth/whatsapp/device/status', { signal });
      setWhatsAppStatus(data);
    } catch (err) {
      console.error('Failed to load WhatsApp status:', err);
    }
  }, []);



  useEffect(() => {
    const controller = new AbortController();
    loadMessages(controller.signal);
    loadWhatsAppStatus(controller.signal);
    return () => controller.abort();
  }, [loadMessages, loadWhatsAppStatus]);

  // Manual verification Step 1: Send code to phone via WhatsApp
  const submitManualVerification = async () => {
    if (!manualPhone.trim()) {
      setWaError(t('messages.whatsapp.phoneRequired') || 'Phone number is required');
      return;
    }
    
    setWaLoading(true);
    setWaError('');
    
    try {
      const fullPhone = `${manualCountryCode}${manualPhone.replace(/^0+/, '')}`;
      const { data } = await apiClient.post<{ challengeId: string; maskedPhone?: string; deliveryChannel?: string }>('/api/auth/whatsapp/device/manual-start', {
        phone: fullPhone,
        forceSms: forceSms
      });
      
      // Store challenge ID and move to code entry step
      setManualChallengeId(data.challengeId);
      setManualMaskedPhone(data.maskedPhone || fullPhone);
      setDeliveryChannel(data.deliveryChannel || (forceSms ? 'SMS' : 'WhatsApp'));
      setWaStep('code');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setWaError(e.response?.data?.error || 'Failed to send verification code');
    } finally {
      setWaLoading(false);
    }
  };

  // Manual verification Step 2: Verify the code
  const submitManualCode = async () => {
    if (!manualChallengeId || !manualCode.trim()) {
      setWaError('Verification code is required');
      return;
    }

    setWaLoading(true);
    setWaError('');

    try {
      await apiClient.post('/api/auth/whatsapp/device/manual-verify', {
        challengeId: manualChallengeId,
        code: manualCode.trim()
      });

      setWaStep('verified');
      // Reload status after showing success
      setTimeout(async () => {
        await loadWhatsAppStatus();
        setShowWhatsAppModal(false);
        resetWhatsAppModal();
      }, 2000);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setWaError(e.response?.data?.error || 'Invalid verification code');
    } finally {
      setWaLoading(false);
    }
  };

  const resetWhatsAppModal = () => {
    setWaStep('initial');
    setWaError('');
    setManualPhone('');
    setManualChallengeId(null);
    setManualCode('');
    setManualMaskedPhone('');
    setForceSms(false);
    setDeliveryChannel('');
  };

  // Submit personal name for multi-device identification
  const submitPersonalName = async () => {
    const name = nameInput.trim();
    if (!name || nameSubmitting) return;
    setNameSubmitting(true);
    try {
      const { data } = await apiClient.post<{ personalName: string }>('/api/v1/guest/messages/set-name', { name });
      setPersonalName(data.personalName);
      setNeedsName(false);
    } catch (err) {
      console.error('Failed to set name:', err);
    } finally {
      setNameSubmitting(false);
    }
  };

  // Listen for new messages via SignalR
  useEffect(() => {
    const unsub = on('NewMessage', (rawData: unknown) => {
      const data = rawData as { id: string; senderType: string; senderName?: string; content: string; sentAt: string; attachments?: Message['attachments']; metadata?: string | null; replyTo?: Message['replyTo'] };
      setMessages(prev => [...prev, {
        id: data.id,
        senderType: data.senderType,
        senderName: data.senderName,
        content: data.content,
        sentAt: data.sentAt,
        attachments: data.attachments,
        replyTo: data.replyTo ?? null,
        metadata: data.metadata,
        parsedMetadata: parseMetadata(data.metadata),
      }]);
      // A menu, laundry or photo card needs the guest's hands: show the chat it landed in.
      const card = parseMetadata(data.metadata);
      if (callFocusRef.current && (card?.menuCard || card?.laundryMenuCard || card?.photoCart)) setCallFocus(false);
      // Play sound for staff messages
      if (data.senderType !== 'Guest') {
        playSound('message-received');
      }
      // Mark as read since we're viewing
      apiClient.post('/api/v1/guest/messages/read').catch(() => {});
    });
    return () => unsub();
  }, [on]);

  // Listen for order status changes via SignalR
  useEffect(() => {
    const unsub = on('OrderStatusChanged', (data) => {
      const orderId = data.orderId as string;
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'order' && msg.parsedMetadata.statusCard.referenceId === orderId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Listen for housekeeping request status changes via SignalR
  useEffect(() => {
    const unsub = on('RequestStatusChanged', (data) => {
      const requestId = data.requestId as string;
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'housekeeping' && msg.parsedMetadata.statusCard.referenceId === requestId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Listen for maintenance report status changes via SignalR
  useEffect(() => {
    const unsub = on('ReportStatusChanged', (data) => {
      const reportId = data.reportId as string;
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'maintenance' && msg.parsedMetadata.statusCard.referenceId === reportId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Listen for reservation status changes via SignalR
  useEffect(() => {
    const unsub = on('ReservationStatusChanged', (data) => {
      const requestId = String(data.requestId);
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'reservation' && msg.parsedMetadata.statusCard.referenceId === requestId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Listen for service request status changes via SignalR
  useEffect(() => {
    const unsub = on('ServiceRequestStatusChanged', (data) => {
      const requestId = String(data.requestId);
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'service' && msg.parsedMetadata.statusCard.referenceId === requestId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Listen for laundry status changes via SignalR
  useEffect(() => {
    const unsub = on('LaundryStatusChanged', (data) => {
      const orderId = String(data.orderId);
      const status = data.status as string;
      setMessages(prev => prev.map(msg => {
        if (msg.parsedMetadata?.statusCard?.type === 'laundry' && msg.parsedMetadata.statusCard.referenceId === orderId) {
          return { ...msg, parsedMetadata: { ...msg.parsedMetadata, statusCard: { ...msg.parsedMetadata.statusCard, status } } };
        }
        return msg;
      }));
    });
    return () => unsub();
  }, [on]);

  // Auto-scroll when a new message arrives or loading finishes — not when earlier ones are added above.
  useEffect(() => {
    if (!loading && messages.length > 0) {
      const lastId = messages[messages.length - 1].id;
      if (lastId !== lastMessageIdRef.current || initialLoadRef.current) {
        lastMessageIdRef.current = lastId;
        scrollToBottom(initialLoadRef.current);
        initialLoadRef.current = false;
      }
    }
  }, [messages, loading]);

  const loadOlder = async () => {
    if (loadingOlder || !hasOlder) return;
    setLoadingOlder(true);
    try {
      const next = olderPage + 1;
      const { data } = await apiClient.get<{ messages: Message[]; hasMore?: boolean }>(`/api/v1/guest/messages/conversation?page=${next}&pageSize=50`);
      const el = containerRef.current;
      const before = el?.scrollHeight ?? 0;
      const older = (data.messages || []).map((m: Message) => ({ ...m, parsedMetadata: parseMetadata(m.metadata) }));
      setMessages(prev => {
        const ids = new Set(prev.map(m => m.id));
        return [...older.filter(m => !ids.has(m.id)), ...prev];
      });
      setOlderPage(next);
      setHasOlder(!!data.hasMore);
      requestAnimationFrame(() => { if (el) el.scrollTop += el.scrollHeight - before; });
    } catch (err) {
      console.error('Failed to load earlier messages:', err);
    } finally {
      setLoadingOlder(false);
    }
  };

  const sendMessage = async () => {
    const content = input.trim();
    const hasFiles = pendingFiles.some(f => f.url);
    if ((!content && !hasFiles) || sending) return;

    setSending(true);
    setInput('');

    const uploadedFiles = pendingFiles.filter(f => f.url);
    const attachments = uploadedFiles.map(f => ({
      fileUrl: f.url!,
      fileName: f.file.name,
      contentType: f.file.type || 'application/octet-stream',
      fileSize: f.file.size,
    }));

    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    const quoted = replyingTo;
    setReplyingTo(null);
    setMessages(prev => [...prev, {
      id: tempId,
      senderType: 'Guest',
      content: content || '',
      sentAt: new Date().toISOString(),
      attachments: attachments.length > 0 ? attachments.map(a => ({ ...a, fileSize: a.fileSize })) : undefined,
      replyTo: quoted ? { id: quoted.id, senderType: quoted.senderType, senderName: quoted.senderName, snippet: (quoted.content || '📎').slice(0, 140) } : null,
    }]);
    setPendingFiles([]);

    try {
      const { data } = await apiClient.post<{ id: string; sentAt: string; attachments?: Message['attachments'] }>('/api/v1/guest/messages/send', {
        content: content || '',
        attachments: attachments.length > 0 ? attachments : undefined,
        replyToMessageId: quoted && !quoted.id.startsWith('temp-') ? quoted.id : undefined,
      });
      // Replace temp message with real one
      setMessages(prev => prev.map(m =>
        m.id === tempId ? { ...m, id: data.id, sentAt: data.sentAt, attachments: data.attachments } : m
      ));
    } catch (err) {
      // Remove failed message
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setInput(content); // restore input
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
      // Refocus the input after sending
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // A photo shown to the AI during a call goes to the chat like any photo; the server passes its result into the call.
  const sendCallPhoto = async (file: File) => {
    // In the thread at once, from the phone, while it compresses and uploads — then swapped for the sent one.
    const tempId = `temp-${Date.now()}`;
    const preview = URL.createObjectURL(file);
    setMessages(prev => [...prev, {
      id: tempId, senderType: 'Guest', content: '', sentAt: new Date().toISOString(), sendingPhoto: preview,
    }]);
    try {
      const { compressFile } = await import('../utils/compressMedia');
      const compressed = await compressFile(file);
      const form = new FormData();
      form.append('file', compressed);
      const { data: uploaded } = await apiClient.post<{ fileUrl: string }>('/api/v1/guest/messages/upload', form);
      const attachment = {
        fileUrl: uploaded.fileUrl,
        fileName: file.name || 'photo.jpg',
        contentType: compressed.type || file.type || 'image/jpeg',
        fileSize: compressed.size,
      };
      const { data } = await apiClient.post<{ id: string; sentAt: string; attachments?: Message['attachments'] }>(
        '/api/v1/guest/messages/send', { content: '', attachments: [attachment] });
      setMessages(prev => prev.map(m => m.id === tempId ? {
        id: data.id, senderType: 'Guest', content: '', sentAt: data.sentAt, attachments: data.attachments ?? [attachment],
      } : m));
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      throw err;
    } finally {
      URL.revokeObjectURL(preview);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';

    const allowedTypes = ['image/', 'video/', 'audio/', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats'];
    for (const file of files.slice(0, 5)) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`File "${file.name}" exceeds 10 MB limit`);
        continue;
      }
      if (!allowedTypes.some(t => file.type.startsWith(t))) {
        alert(`File type "${file.type || 'unknown'}" is not supported`);
        continue;
      }

      const entry = { file, uploading: true, url: undefined as string | undefined, error: undefined as string | undefined };
      setPendingFiles(prev => [...prev, entry]);

      try {
        // Compress images/videos before upload
        const { compressFile } = await import('../utils/compressMedia');
        const compressed = await compressFile(file);

        const form = new FormData();
        form.append('file', compressed);
        const { data } = await apiClient.post<{ fileUrl: string }>('/api/v1/guest/messages/upload', form);
        setPendingFiles(prev => prev.map(f => f.file === file ? { ...f, uploading: false, url: data.fileUrl } : f));
      } catch {
        setPendingFiles(prev => prev.map(f => f.file === file ? { ...f, uploading: false, error: 'Upload failed' } : f));
      }
    }
  };

  const removePendingFile = (file: File) => {
    setPendingFiles(prev => prev.filter(f => f.file !== file));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <div ref={pageRef} className={`messages-page${callOpen && callFocus ? ' messages-page-call-focus' : ''}`}>
      {/* No page header: the chat starts under the top bar, which carries the WhatsApp state and the call button. */}
      {navActions && createPortal(
        <div className="messages-nav-actions">
          {whatsAppStatus && !whatsAppStatus.isVerified && (
            <button
              className="header-whatsapp-btn"
              onClick={() => setShowWhatsAppModal(true)}
            >
              📱 {t('messages.whatsapp.verifyButton') || 'Verify WhatsApp'}
            </button>
          )}
          {whatsAppStatus?.isVerified && (
            <span className="header-whatsapp-verified">✓ WhatsApp</span>
          )}
          {voiceCallAvailable && !callOpen && (
            <button className="header-call-btn" onClick={() => setCallOpen(true)} aria-label="Call the assistant">📞</button>
          )}
        </div>,
        navActions
      )}

      {callOpen && (
        <CallDock onClose={closeCall} onSendPhoto={sendCallPhoto} focus={callFocus} onFocusChange={setCallFocus} />
      )}

      {/* Personal Name Prompt (multi-device rooms) */}
      {needsName && !personalName && (
        <div className="verification-overlay">
          <div className="verification-modal" onClick={e => e.stopPropagation()}>
            <div className="verification-icon">👋</div>
            <h3 className="verification-title">{t('messages.namePrompt.title') || "What's your name?"}</h3>
            <p className="verification-subtitle">
              {t('messages.namePrompt.description') || 'Multiple devices are connected to this room. Please enter your name so staff can identify you.'}
            </p>
            <input
              type="text"
              className="code-input"
              placeholder={t('messages.namePrompt.placeholder') || 'e.g. Sarah, Dad, Room 103 - Mom'}
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitPersonalName(); }}
              disabled={nameSubmitting}
              autoFocus
              style={{
                textAlign: 'center',
                fontSize: '1.1rem',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                width: '100%',
                marginBottom: '16px'
              }}
            />
            <button
              className="verification-btn primary"
              onClick={submitPersonalName}
              disabled={nameSubmitting || !nameInput.trim()}
            >
              {nameSubmitting ? <span className="spinner-border spinner-border-sm" /> : (t('messages.namePrompt.submit') || 'Continue')}
            </button>
          </div>
        </div>
      )}

      {/* WhatsApp Verification Modal */}
      {showWhatsAppModal && (
        <div className="verification-overlay" onClick={() => { setShowWhatsAppModal(false); resetWhatsAppModal(); }}>
          <div className="verification-modal" onClick={e => e.stopPropagation()}>
            <button className="verification-close" onClick={() => { setShowWhatsAppModal(false); resetWhatsAppModal(); }}>×</button>
            
            {waStep === 'initial' && (
              <>
                <div className="verification-icon">📱</div>
                <h3 className="verification-title">{t('messages.whatsapp.modalTitle') || 'Connect WhatsApp'}</h3>
                <p className="verification-subtitle">
                  {t('messages.whatsapp.connectDescription') || 'Enter your WhatsApp number to receive messages from the hotel.'}
                </p>

                <div className="phone-input-group">
                  <select
                    className="country-select"
                    value={manualCountryCode}
                    onChange={e => setManualCountryCode(e.target.value)}
                    disabled={waLoading}
                  >
                    <option value="+972">🇮🇱 +972</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+7">🇷🇺 +7</option>
                    <option value="+90">🇹🇷 +90</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+966">🇸🇦 +966</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+82">🇰🇷 +82</option>
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+31">🇳🇱 +31</option>
                    <option value="+30">🇬🇷 +30</option>
                    <option value="+48">🇵🇱 +48</option>
                  </select>
                  <input
                    type="tel"
                    inputMode="tel"
                    className="phone-input"
                    placeholder={t('messages.whatsapp.phonePlaceholder') || '501234567'}
                    value={manualPhone}
                    onChange={e => setManualPhone(e.target.value.replace(/\D/g, ''))}
                    disabled={waLoading}
                  />
                </div>

                {waError && <div className="verification-error">{waError}</div>}

                <button
                  className="verification-btn primary"
                  onClick={submitManualVerification}
                  disabled={waLoading || !manualPhone.trim()}
                >
                  {waLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    forceSms 
                      ? (t('messages.whatsapp.sendViaSms') || 'Send Code via SMS')
                      : (t('messages.whatsapp.sendVerificationCode') || 'Send Verification Code')
                  )}
                </button>

                {!forceSms ? (
                  <button 
                    className="verification-btn link" 
                    onClick={() => setForceSms(true)}
                    style={{ fontSize: '0.85rem', color: '#6c757d' }}
                  >
                    {t('messages.whatsapp.useSmsInstead') || "WhatsApp not working? Use SMS instead"}
                  </button>
                ) : (
                  <button 
                    className="verification-btn link" 
                    onClick={() => setForceSms(false)}
                    style={{ fontSize: '0.85rem', color: '#6c757d' }}
                  >
                    {t('messages.whatsapp.useWhatsAppInstead') || "← Use WhatsApp instead"}
                  </button>
                )}

                <button className="verification-btn link" onClick={() => { setShowWhatsAppModal(false); resetWhatsAppModal(); }}>
                  {t('messages.whatsapp.skipForNow') || 'Skip for now'}
                </button>
              </>
            )}

            {waStep === 'code' && (
              <>
                <div className="verification-icon">{deliveryChannel === 'SMS' ? '📱' : '💬'}</div>
                <h3 className="verification-title">{t('messages.whatsapp.enterCode') || 'Enter Verification Code'}</h3>
                <p className="verification-subtitle">
                  {deliveryChannel === 'SMS' 
                    ? (t('messages.whatsapp.codeSentViaSms') || 'We sent a code via SMS to')
                    : (t('messages.whatsapp.codeSentViaWhatsApp') || 'We sent a code via WhatsApp to')
                  } <strong>{manualMaskedPhone}</strong>
                </p>
                {deliveryChannel !== 'SMS' && (
                  <p className="verification-hint" style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '16px' }}>
                    {t('messages.whatsapp.checkWhatsApp') || 'Check your WhatsApp messages for the code.'}
                  </p>
                )}

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="code-input"
                  placeholder="000000"
                  maxLength={6}
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value.replace(/\D/g, ''))}
                  disabled={waLoading}
                  style={{ 
                    textAlign: 'center', 
                    fontSize: '1.5rem', 
                    letterSpacing: '0.3em',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    width: '100%',
                    marginBottom: '16px'
                  }}
                />

                {waError && <div className="verification-error">{waError}</div>}

                <button
                  className="verification-btn primary"
                  onClick={submitManualCode}
                  disabled={waLoading || manualCode.trim().length !== 6}
                >
                  {waLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    t('messages.whatsapp.verify') || 'Verify'
                  )}
                </button>

                <button 
                  className="verification-btn link" 
                  onClick={() => { setWaStep('initial'); setWaError(''); setManualCode(''); }}
                >
                  ← {t('messages.whatsapp.changeNumber') || 'Change number'}
                </button>
              </>
            )}

            {waStep === 'verified' && (
              <>
                <div className="verification-icon success">✓</div>
                <h3 className="verification-title">{t('messages.whatsapp.verified') || 'WhatsApp Connected!'}</h3>
                <p className="verification-subtitle">
                  {t('messages.whatsapp.verifiedDescription') || 'You will now receive hotel messages on your WhatsApp.'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="messages-container" ref={containerRef}
        onScroll={e => { if (e.currentTarget.scrollTop < 60) loadOlder(); }}>
        {hasOlder && (
          <div style={{ textAlign: 'center', padding: '6px 0' }}>
            <button type="button" className="btn btn-link btn-sm" onClick={loadOlder} disabled={loadingOlder}>
              {loadingOlder ? '…' : (t('messages.earlier') !== 'messages.earlier' ? t('messages.earlier') : 'Earlier messages')}
            </button>
          </div>
        )}
        {messages.length === 0 ? (
          <div className="messages-empty">
            <span className="messages-empty-icon">💬</span>
            <p>{t('messages.empty') || 'No messages yet. Send a message to start a conversation!'}</p>
          </div>
        ) : (
          <div className="messages-list">
            {(() => {
              // Find last AI message index for interactive options
              let lastAiIdx = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].senderType !== 'Guest') { lastAiIdx = i; break; }
              }
              return messages.map((msg, idx) => {
              const hasTracker = !!(msg.parsedMetadata?.statusCard || msg.parsedMetadata?.menuCard || msg.parsedMetadata?.laundryMenuCard || msg.parsedMetadata?.photoCart);
              const hasInteractiveCard = !!(msg.parsedMetadata?.menuCard || msg.parsedMetadata?.laundryMenuCard);
              const isLastAi = idx === lastAiIdx;
              const options = msg.senderType !== 'Guest' ? parseNumberedOptions(msg.content) : [];

              const handleQuickReply = (opt: { number: string; label: string }) => {
                if (sending) return;
                setInput(opt.number);
                // Auto-send after setting input
                setTimeout(() => {
                  const content = opt.number;
                  if (!content) return;
                  setSending(true);
                  setInput('');
                  const tempId = `temp-${Date.now()}`;
                  setMessages(prev => [...prev, {
                    id: tempId,
                    senderType: 'Guest',
                    content,
                    sentAt: new Date().toISOString(),
                  }]);
                  apiClient.post<{ id: string; sentAt: string }>('/api/v1/guest/messages/send', { content })
                    .then(({ data }) => {
                      setMessages(prev => prev.map(m =>
                        m.id === tempId ? { ...m, id: data.id, sentAt: data.sentAt } : m
                      ));
                    })
                    .catch(() => {
                      setMessages(prev => prev.filter(m => m.id !== tempId));
                    })
                    .finally(() => setSending(false));
                }, 0);
              };

              const stage = stageAt(stages, msg.sentAt);
              const prevStage = idx > 0 ? stageAt(stages, messages[idx - 1].sentAt) : null;
              const stageDivider = stages.length > 1 && stage && stage.stage !== prevStage?.stage
                ? stageLabel(stage, t) : null;

              return (
                <div key={msg.id} className={`message-row ${hasTracker ? 'message-row--has-tracker' : ''} ${stage ? `message-row--${stage.stage}` : ''}`}>
                  {stageDivider && (
                    <div className={`message-stage-divider message-stage-divider--${stage!.stage}`}>
                      <span>{stageDivider}</span>
                    </div>
                  )}
                  <div
                    id={`msg-${msg.id}`}
                    className={`message-bubble ${msg.senderType === 'Guest' ? 'sent' : 'received'}`}
                    style={{ WebkitTouchCallout: 'none' }}
                    onTouchStart={() => !msg.id.startsWith('temp-') && startPress(msg.id)}
                    onTouchEnd={endPress}
                    onTouchMove={endPress}
                    onContextMenu={e => { if (reactingTo === msg.id) e.preventDefault(); }}
                  >
                    {msg.senderType !== 'Guest' && msg.senderName && (
                      <span className="message-sender">{msg.senderName}</span>
                    )}
                    {msg.replyTo && (
                      <button type="button" className="message-quote"
                        onClick={() => document.getElementById(`msg-${msg.replyTo!.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                        <span className="message-quote-name">
                          {msg.replyTo.senderType === 'Guest' ? (t('messages.you') !== 'messages.you' ? t('messages.you') : 'You') : (msg.replyTo.senderName || hotelReplyName(t))}
                        </span>
                        <span className="message-quote-text">{msg.replyTo.snippet}</span>
                      </button>
                    )}
                    <div className="message-content">{
                      msg.parsedMetadata?.photoCart
                        // The card shows the cart; the bubble keeps only its opening line.
                        ? msg.content.split('\n')[0].replace(/\*/g, '')
                        : hasInteractiveCard
                        ? msg.content.split('\n').filter(line => !/^\s*\d+[.)]\s+/.test(line)).join('\n').trim()
                        : isLastAi && options.length > 0 && !sending && !hasInteractiveCard
                          ? renderInlineOptions(msg.content, options, handleQuickReply)
                          : msg.content
                    }</div>
                    {msg.sendingPhoto && (
                      <div className="message-photo-sending">
                        <img src={msg.sendingPhoto} alt="" />
                        <span className="message-photo-sending-label">
                          <span className="message-photo-sending-spinner" />
                          {t('messages.sendingPhoto') !== 'messages.sendingPhoto' ? t('messages.sendingPhoto') : 'Sending photo…'}
                        </span>
                      </div>
                    )}
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="message-attachments">
                        {msg.attachments.map((att, ai) => (
                          <SecureAttachment
                            key={ai}
                            fileUrl={att.fileUrl}
                            fileName={att.fileName}
                            contentType={att.contentType}
                            fileSize={att.fileSize}
                            isSent={msg.senderType === 'Guest'}
                          />
                        ))}
                      </div>
                    )}
                    <span className="message-time">
                      {formatTime(msg.sentAt)}
                      {/* Desktop only (shown on hover): reply and react. On touch, a long-press opens the menu below. */}
                      {!msg.id.startsWith('temp-') && (
                        <span className="message-actions">
                          <button type="button" className="message-reply-btn" aria-label="Reply"
                            onClick={() => { setReplyingTo(msg); setTimeout(() => inputRef.current?.focus(), 30); }}>↩</button>
                          <button type="button" className="message-reply-btn" aria-label="React"
                            onClick={e => { e.stopPropagation(); openedAt.current = Date.now(); setReactingTo(reactingTo === msg.id ? null : msg.id); }}>☺</button>
                        </span>
                      )}
                    </span>
                    {reactingTo === msg.id && (
                      <div className="message-menu" onClick={e => e.stopPropagation()}>
                        <div className="message-reaction-picker">
                          {['👍', '❤️', '😂', '😮', '🙏', '👎'].map(e => (
                            <button key={e} type="button" onClick={() => react(msg.id, e)}>{e}</button>
                          ))}
                        </div>
                        <button type="button" className="message-menu-item"
                          onClick={() => { setReactingTo(null); setReplyingTo(msg); setTimeout(() => inputRef.current?.focus(), 30); }}>
                          ↩ {t('messages.reply') !== 'messages.reply' ? t('messages.reply') : 'Reply'}
                        </button>
                      </div>
                    )}
                  </div>
                  {msg.reactions && msg.reactions.length > 0 && (
                    <div className={`message-reactions ${msg.senderType === 'Guest' ? 'sent' : 'received'}`}>
                      {msg.reactions.map(r => (
                        <button key={r.emoji} type="button" className="message-reaction-chip" onClick={() => react(msg.id, r.emoji)}>
                          {r.emoji}{r.count > 1 ? ` ${r.count}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Interactive Status Card — full width, outside bubble */}
                  {msg.parsedMetadata?.statusCard && (
                    <div className="message-tracker-wrapper">
                      <StatusCard metadata={msg.parsedMetadata.statusCard} />
                    </div>
                  )}
                  {/* Interactive Menu Card — full width, outside bubble */}
                  {msg.parsedMetadata?.menuCard && (
                    <div className="message-tracker-wrapper">
                      <MenuCard
                        metadata={msg.parsedMetadata.menuCard}
                        messageId={msg.id}
                        onOrderPlaced={(_orderId, statusMeta) => {
                          if (statusMeta) {
                            setMessages(prev => prev.map(m =>
                              m.id === msg.id
                                ? { ...m, parsedMetadata: { statusCard: statusMeta } }
                                : m
                            ));
                          }
                        }}
                      />
                    </div>
                  )}
                  {/* Interactive Laundry Menu Card — full width, outside bubble */}
                  {msg.parsedMetadata?.laundryMenuCard && (
                    <div className="message-tracker-wrapper">
                      <LaundryMenuCard
                        metadata={msg.parsedMetadata.laundryMenuCard}
                        messageId={msg.id}
                        onOrderPlaced={(_orderId, statusMeta) => {
                          if (statusMeta) {
                            setMessages(prev => prev.map(m =>
                              m.id === msg.id
                                ? { ...m, parsedMetadata: { statusCard: statusMeta } }
                                : m
                            ));
                          }
                        }}
                      />
                    </div>
                  )}
                  {/* Cart built from the guest's product photos — Confirm / Cancel */}
                  {msg.parsedMetadata?.photoCart && (
                    <div className="message-tracker-wrapper">
                      <PhotoCartCard
                        metadata={msg.parsedMetadata.photoCart}
                        onDecided={next => setMessages(prev => prev.map(m =>
                          m.id === msg.id ? { ...m, parsedMetadata: { photoCart: next } } : m))}
                      />
                    </div>
                  )}
                  {/* Quick-reply options are now rendered inline inside the message bubble */}
                </div>
              );
            });
            })()}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="messages-input-area">
        {/* Pending file previews */}
        {pendingFiles.length > 0 && (
          <div className="messages-pending-files">
            {pendingFiles.map((pf, i) => (
              <div key={i} className="pending-file-chip">
                {pf.uploading ? (
                  <span className="pending-file-spinner" />
                ) : pf.error ? (
                  <span className="pending-file-error">⚠️</span>
                ) : (
                  <span>📄</span>
                )}
                <span className="pending-file-name">{pf.file.name}</span>
                <button className="pending-file-remove" onClick={() => removePendingFile(pf.file)}>✕</button>
              </div>
            ))}
          </div>
        )}
        {replyingTo && (
          <div className="messages-replying">
            <div className="messages-replying-text">
              <span className="message-quote-name">
                {replyingTo.senderType === 'Guest' ? (t('messages.you') !== 'messages.you' ? t('messages.you') : 'You') : (replyingTo.senderName || hotelReplyName(t))}
              </span>
              <span className="message-quote-text">{(replyingTo.content || '📎').slice(0, 140)}</span>
            </div>
            <button type="button" className="pending-file-remove" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">✕</button>
          </div>
        )}
        <div className="messages-input-row">
          <input ref={fileInputRef} type="file" multiple className="messages-file-input-hidden" onChange={handleFileSelect} />
          <button className="messages-attach-btn" onClick={() => fileInputRef.current?.click()} title="Attach file">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          {isRecording ? (
            <div className="messages-recording-indicator">
              <span className="recording-dot" />
              <span className="recording-time">{formatRecordingTime(recordingDuration)}</span>
              <button className="recording-cancel-btn" onClick={cancelRecording} title="Cancel recording">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <button className="recording-stop-btn" onClick={stopRecording} title="Stop and transcribe">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </button>
            </div>
          ) : (
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isTranscribing ? 'Transcribing...' : (t('messages.placeholder') || 'Type a message...')}
              rows={1}
              maxLength={1000}
              disabled={sending || isTranscribing}
            />
          )}
          {!isRecording && (
            <button
              className={`messages-mic-btn${isTranscribing ? ' transcribing' : ''}`}
              onClick={startRecording}
              disabled={sending || isTranscribing}
              title={isTranscribing ? 'Transcribing...' : 'Record voice message'}
            >
              {isTranscribing ? (
                <span className="mic-spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>
          )}
          <button
            className="messages-send-btn"
            onClick={sendMessage}
            disabled={(!input.trim() && !pendingFiles.some(f => f.url)) || sending || isRecording}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface StageSpan { stage: 'pre_arrival' | 'in_house' | 'post_stay'; from: string | null }

/** The stage a message was sent in: the last span that began at or before it. */
function stageAt(stages: StageSpan[], sentAt: string): StageSpan | null {
  if (stages.length === 0) return null;
  const at = new Date(sentAt).getTime();
  let current: StageSpan | null = null;
  for (const s of stages) if (s.from == null || new Date(s.from).getTime() <= at) current = s;
  return current ?? stages[0];
}

function stageLabel(span: StageSpan, t: (key: string) => string): string {
  const pick = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);
  const label = span.stage === 'pre_arrival' ? pick('messages.stageBeforeArrival', 'Before arrival')
    : span.stage === 'in_house' ? pick('messages.stageCheckedIn', 'Checked in')
    : pick('messages.stageCheckedOut', 'Checked out');
  const when = span.from ? ` · ${new Date(span.from).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : '';
  return label + when;
}

function hotelReplyName(t: (key: string) => string): string {
  return t('messages.hotel') !== 'messages.hotel' ? t('messages.hotel') : 'Hotel';
}

